import React, { useState } from "react";
import { Construction, PencilRuler } from "lucide-react";
import { motion } from "framer-motion";

const services = [
  {
    title: "Construction",
    items: [
      "Residential Building",
      "Commercial Building",
      "Water Tank",
      "Retaining Wall",
      "Trimix Flooring",
      "Compound Wall",
      "Paving Block",
      "All Fabrication",
      "Water Proofing",
      "Renovation",
      "Chainlink Fence",
    ],
    icon: Construction,
    gradient: "from-blue-600 to-blue-900",
    image: "/dist/assets/construction.jpg",
  },
  {
    title: "Design",
    items: [
      "Residential Building",
      "Commercial Building",
      "Architectural Planning",
      "Retaining Wall",
      "Industrial Shed",
      "Water Tank",
      "Structural Audit",
      "Interior Design",
    ],
    icon: PencilRuler,
    gradient: "from-blue-400 to-blue-700",
    image: "/dist/assets/design.jpg",
  },
];

const Services = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">Our Services</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive construction and design solutions tailored to your needs
          </p>
        </div>

        {/* Services Cards */}
        <div className="grid md:grid-cols-2 gap-12">
          {services.map((service, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="relative bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-gray-200 hover:border-blue-500 transform transition-transform duration-500 hover:scale-105 hover:rotate-1 hover:shadow-2xl"
            >
              {/* Background Image */}
              <div className="absolute inset-0 z-10">
                <motion.div
                  initial={{ opacity: 1 }}
                  animate={{
                    opacity: hoveredIndex === index ? 0.3 : 1,
                    scale: hoveredIndex === index ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full"
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>

              {/* Card Content */}
              <motion.div
                className="relative z-20 p-8"
                initial={{ opacity: 0, y: -20 }}
                animate={{
                  opacity: hoveredIndex === index ? 1 : 0,
                  y: hoveredIndex === index ? 0 : -20,
                }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center mb-6">
                  <service.icon className="w-10 h-10 text-gray-700 mr-4" />
                  <h3 className="text-3xl font-bold text-gray-900">{service.title}</h3>
                </div>

                {/* List of Items */}
                <ul className="space-y-4">
                  {service.items.map((item, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{
                        opacity: hoveredIndex === index ? 1 : 0,
                        x: hoveredIndex === index ? 0 : -20,
                      }}
                      transition={{ duration: 0.3, delay: idx * 0.1 }}
                      className="flex items-center text-lg text-gray-700"
                    >
                      <span className="w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
